import { ReactNode, useCallback, useMemo, useState } from 'react';
import MuiSelect, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Arrow from '@/assets/icons/arrow-down-black.svg';
import { styled } from '@mui/material/styles';
import Alert from '../alert';
import './index.css';
export interface ISelectOption {
    label: string;
    value: string | number;
}

export type ISelectOptions = ISelectOption[];

interface IProps extends Omit<SelectProps, 'placeholder' | 'onChange' | 'label'> {
    label?: ReactNode | ((error: boolean, boldLabel: boolean, required?: boolean) => ReactNode);
    placeholder?: string;
    options?: ISelectOptions;
    required?: boolean;
    boldLabel?: boolean;
    labelClassName?: string;
    labelToReflectError?: boolean;
    wrapperClassName?: string;
    error?: boolean;
    errorMessage?: string;
    testId?: string;
    onChange?: (value: string) => void;
    alertClassName?: string;
    /** set true to skip applying the ph-no-capture masking class, e.g. for non-PII filter dropdowns */
    disableMasking?: boolean;
}

const Icon = styled('img')<{ open: boolean }>(({ open }) => ({
    width: '16px',
    height: '16px',
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s ease'
}));

export const Select = ({
    wrapperClassName = '',
    placeholder,
    options,
    label,
    boldLabel = false,
    labelClassName = '',
    labelToReflectError = false,
    error,
    errorMessage,
    required = false,
    sx,
    onChange,
    testId = 'base-select',
    alertClassName = '',
    className = '',
    SelectDisplayProps,
    disableMasking = false,
    ...props
}: IProps) => {
    const [open, setOpen] = useState(false);

    const labelRenderer = useMemo(() => {
        if (!label) return null;
        if (typeof label === 'function') {
            return label(!!error, boldLabel, required);
        }
        return (
            <>
                {required && <span className="font-bold text-red-500">*</span>}
                <span
                    className={`flex flex-row items-center ${boldLabel ? 'text-base font-bold lg:text-lg' : 'text-lg lg:text-xl'
                        } ${labelToReflectError && error ? 'text-red-500' : ''} ${labelClassName} font-atkinsonHyperlegible`}
                >
                    {label}
                </span>
            </>
        );
    }, [boldLabel, error, label, labelClassName, required, labelToReflectError]);

    const handleOnChange = useCallback(
        (e: SelectChangeEvent<unknown>) => {
            onChange?.(String(e.target.value));
        },
        [onChange]
    );

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    const border = error ? '1px solid red' : `1px solid rgba(0, 0, 0, 0.6)`;
    const hasValue = props.value !== undefined && props.value !== null && props.value !== '';
    return (
        <div className={`flex flex-col ${wrapperClassName ? wrapperClassName : 'min-w-56 max-w-full'}`} data-testid={`${testId}-container`}>
            {labelRenderer && (
                <div data-testid={`${testId}-title`} className="mb-2 flex items-center gap-2">
                    {labelRenderer}
                </div>
            )}
            <MuiSelect
                displayEmpty
                error={error}
                className={className}
                // only mask the display div once a value is selected, so the empty placeholder stays visible in recordings
                SelectDisplayProps={{
                    ...SelectDisplayProps,
                    className: `${hasValue && !disableMasking ? 'ph-no-capture' : ''} ${SelectDisplayProps?.className ?? ''}`.trim()
                }}
                onOpen={handleOpen}
                onClose={handleClose}
                data-testid={`${testId}-select`}
                IconComponent={() => (
                    <div
                        className="flex h-[100%] min-h-[30px] items-center justify-center"
                        onClick={() => {
                            setOpen(!open);
                        }}
                    >
                        <Icon alt="dropdown icon" open={open} src={Arrow} style={{ cursor: 'pointer' }} />
                    </div>
                )}
                open={open}
                inputProps={{ 'aria-label': 'Without label' }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            bgcolor: 'transparent',
                            paddingTop: '1px',
                            boxShadow: '0px 4px 4px 2px #00000005',
                            border: '0.5px solid #999999',
                            borderTopWidth: 0,
                            borderRadius: '0 0 8px 8px',
                            '& .MuiList-root': {
                                padding: 0,
                                bgcolor: 'white',
                                '& .MuiMenuItem-root ': {
                                    bgcolor: 'transparent',
                                    '&:hover': {
                                        bgcolor: 'primary.50'
                                    }
                                }
                            },
                            '& .MuiMenuItem-root': {
                                fontSize: '16px',
                                padding: '9px 20px',
                                backgroundColor: '#f4f4f4',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0'
                                }
                            }
                        }
                    }
                }}
                onChange={handleOnChange}
                {...props}
                sx={{
                    bgcolor: 'white',
                    padding: '8.5px',
                    maxWidth: '100%',
                    boxBhadow: '0px 1px 4px 0px #0000001A',
                    border,
                    // borderRadius: '8px',
                    zIndex: 20,
                    fieldset: {
                        border: 'none'
                    },
                    '& .MuiSelect-select': {
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '18px',
                        color: '#000'
                    },
                    ...sx
                }}
            >
                <MenuItem data-testid={`${testId}-placeholder`} value="" sx={{ display: 'none' }}>
                    <em className="font-dmSans text-[#999999]">{placeholder}</em>
                </MenuItem>
                {options?.map((option, idx) => (
                    <MenuItem className={disableMasking ? '' : 'ph-no-capture'} data-testid={`${testId}-select-option-${idx}`} key={idx + option.value.toString()} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </MuiSelect>

            {error && errorMessage && <Alert className={`mt-2.5 font-dmSans lg:mt-6 ${alertClassName}`} label={errorMessage} />}
        </div>
    );
};

export default Select;
